import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAppAuth } from '@/hooks/useAppAuth';

export type Notification = {
  id: string;
  user_id: string;
  type: 'match_request' | 'match_accepted' | 'match_rejected' | 'match_completed' | 'review_received' | 'chat_message' | 'system';
  title: string;
  body: string | null;
  related_id: string | null;
  is_read: boolean;
  created_at: string;
};

type NotificationContextType = {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  refresh: () => void;
};

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  loading: false,
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  refresh: () => {},
});

export function NotificationProvider({ children }: { children: ReactNode }) {
  const auth = useAppAuth(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const refresh = useCallback(() => {
    if (!auth.userId) return;
    setLoading(true);
    supabase
      .from('notifications')
      .select('*')
      .eq('user_id', auth.userId)
      .order('created_at', { ascending: false })
      .limit(30)
      .then(({ data }) => {
        if (data) setNotifications(data as Notification[]);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [auth.userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // 실시간 구독: 새 알림이 오면 목록에 즉시 추가
  useEffect(() => {
    if (!auth.userId) return;

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${auth.userId}`,
        },
        (payload) => {
          const newNotif = payload.new as Notification;
          setNotifications((prev) => {
            if (prev.some((n) => n.id === newNotif.id)) return prev;
            return [newNotif, ...prev];
          });
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [auth.userId]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  async function markAsRead(id: string) {
    await supabase.from('notifications').update({ is_read: true }).eq('id', id);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
    );
  }

  async function markAllAsRead() {
    if (!auth.userId) return;
    await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('user_id', auth.userId)
      .eq('is_read', false);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
  }

  return (
    <NotificationContext.Provider
      value={{ notifications, unreadCount, loading, markAsRead, markAllAsRead, refresh }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}

/** 매칭 관련 이벤트 발생 시 알림 생성 (컴포넌트에서 직접 호출) */
export async function createNotification(params: {
  userId: string;
  type: Notification['type'];
  title: string;
  body?: string;
  relatedId?: string;
}) {
  try {
    const { error } = await supabase.from('notifications').insert({
      user_id: params.userId,
      type: params.type,
      title: params.title,
      body: params.body || null,
      related_id: params.relatedId || null,
    });
    if (error) console.error('Failed to create notification:', error);
  } catch (err) {
    console.error('Failed to create notification:', err);
  }
}